/**
 * ProxyNetworkInterceptor — captures HTTP traffic from Android emulators by
 * routing it through a local mockttp proxy.
 *
 * How it works:
 *   1. Starts mockttp as an HTTP proxy (plain TCP, not HTTPS listener).
 *   2. Adds a `forAnyRequest().thenPassThrough()` rule so requests are
 *      forwarded to their real destinations rather than getting 503.
 *   3. Subscribes to `on('request', ...)` and `on('response', ...)` events,
 *      correlating them by ID to build CapturedNetworkRequest entries.
 *   4. Sets the emulator's system http_proxy to 10.0.2.2:<port> so that all
 *      OkHttp/HttpURLConnection traffic from Android apps is intercepted.
 *
 * HTTPS note:
 *   The proxy also installs a generated CA cert to the emulator trust store
 *   (system first, then user-store fallback). When HTTPS MitM is enabled in a
 *   future update (requires emulator launched with `-writable-system` for
 *   system trust), HTTPS traffic will also be captured.
 *
 * For emulators running API 29+ with overlayfs, `adb remount` fails. The
 * implementation falls back to the user cert store
 * (/data/misc/user/0/cacerts-added/), which is trusted by apps that include
 * <certificates src="user"/> in their network security config (all debug builds).
 */

import { execFileSync } from 'node:child_process';
import { writeFileSync, unlinkSync } from 'node:fs';
import { join } from 'node:path';
import { tmpdir } from 'node:os';
import type { CapturedNetworkRequest } from '@testrelic/core';
import {
  redactHeaders,
  redactBodyFields,
  DEFAULT_REDACT_HEADERS,
  DEFAULT_REDACT_BODY_FIELDS,
} from './redaction.js';

// Dynamic import so mockttp is only resolved when actually used.
async function getMockttp() {
  try {
    return await import('mockttp');
  } catch {
    return null;
  }
}

/**
 * Compute the OpenSSL subject_hash_old for a PEM cert.
 * This 8-hex-char value is what Android uses as the system cert filename.
 */
function computeCertHash(certPemPath: string): string | null {
  const candidates = [
    'openssl',
    'C:\\Program Files\\Git\\usr\\bin\\openssl.exe',
    'C:\\Program Files (x86)\\Git\\usr\\bin\\openssl.exe',
  ];
  for (const bin of candidates) {
    try {
      const hash = execFileSync(
        bin,
        ['x509', '-subject_hash_old', '-noout', '-in', certPemPath],
        { encoding: 'utf8', stdio: 'pipe', timeout: 10_000 },
      ).trim();
      if (/^[0-9a-f]{8}$/.test(hash)) return hash;
    } catch {
      // try next candidate
    }
  }
  return null;
}

/** Partial request state stored while waiting for the matching response. */
interface PendingRequest {
  url: string;
  method: string;
  startedAt: string;
  startTimestamp: number;
  requestHeaders: Record<string, string>;
}

export interface ProxyRedactionOptions {
  /** Header names (case-insensitive) whose values are masked before storage. */
  readonly redactHeaders?: readonly string[];
  /** JSON body field names (case-insensitive) masked before storage. */
  readonly redactBodyFields?: readonly string[];
}

export class ProxyNetworkInterceptor {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private proxy: any = null;
  private entries: CapturedNetworkRequest[] = [];
  private readonly deviceSerial: string;
  private started = false;
  private proxyPort = 0;
  private httpsEnabled = false;
  private installedCertPath: string | null = null;
  private readonly redactHeaderList: readonly string[];
  private readonly redactBodyFieldList: readonly string[];

  /** In-flight requests waiting for their response, keyed by mockttp request id. */
  private pendingRequests = new Map<string, PendingRequest>();

  constructor(deviceSerial: string, redaction: ProxyRedactionOptions = {}) {
    this.deviceSerial = deviceSerial;
    this.redactHeaderList = redaction.redactHeaders ?? DEFAULT_REDACT_HEADERS;
    this.redactBodyFieldList = redaction.redactBodyFields ?? DEFAULT_REDACT_BODY_FIELDS;
  }

  async start(): Promise<boolean> {
    const mockttp = await getMockttp();
    if (!mockttp) return false;

    try {
      // -----------------------------------------------------------------------
      // Attempt CA cert installation to device trust store (best-effort)
      // -----------------------------------------------------------------------
      try {
        const ca = await mockttp.generateCACertificate();
        const certFile = join(tmpdir(), `testrelic-ca-${Date.now()}.pem`);
        writeFileSync(certFile, ca.cert);
        const hash = computeCertHash(certFile);

        if (hash) {
          execFileSync('adb', ['-s', this.deviceSerial, 'root'],
            { stdio: 'ignore', timeout: 15_000 });
          await new Promise<void>((r) => setTimeout(r, 1500));
          const certPath = this._installCert(certFile, hash);
          if (certPath) {
            this.installedCertPath = certPath;
            this.httpsEnabled = true;
          }
        }
        try { unlinkSync(certFile); } catch { /* ignore */ }
      } catch (err) {
        process.stderr.write(
          `[TestRelic] CA cert install failed (${(err as Error).message ?? err}). ` +
          'HTTPS capture via trust-store will be unavailable.\n',
        );
      }

      // -----------------------------------------------------------------------
      // Start mockttp as plain HTTP proxy
      // -----------------------------------------------------------------------
      this.proxy = mockttp.getLocal();
      await this.proxy.start(0);
      this.proxyPort = this.proxy.port as number;

      // Forward all requests — without this rule mockttp returns 503 and
      // the on('response') event does NOT fire for those local responses.
      await this.proxy.forAnyRequest().thenPassThrough();

      // Subscribe to request events to record URL/method/headers/timing.
      // The response event only has statusCode/headers/body — no request fields.
      await this.proxy.on('request', (req: ReqLike) => {
        const flatHeaders = flattenHeaders(req.headers ?? {});
        this.pendingRequests.set(req.id, {
          url: req.url ?? '',
          method: req.method ?? 'GET',
          startedAt: new Date(req.timingEvents?.startTime ?? Date.now()).toISOString(),
          startTimestamp: req.timingEvents?.startTimestamp ?? Date.now(),
          requestHeaders: flatHeaders,
        });
      });

      // Subscribe to response events and correlate with the pending request.
      await this.proxy.on('response', (res: ResLike) => {
        this._collectResponse(res).catch(() => { /* silently ignore */ });
      });

      // Set the emulator's system proxy (10.0.2.2 is the AVD host alias)
      this._adbShell([
        'settings', 'put', 'global', 'http_proxy',
        `10.0.2.2:${this.proxyPort}`,
      ]);

      this.started = true;
      return true;
    } catch (err) {
      process.stderr.write(`[TestRelic] Proxy start failed: ${(err as Error).message ?? err}\n`);
      this.started = false;
      return false;
    }
  }

  /** Record the current buffer length at the start of a test. */
  snapshotOffset(): number {
    return this.entries.length;
  }

  /** Returns entries captured since the given offset (start of a test). */
  getEntriesSince(offset: number): CapturedNetworkRequest[] {
    return this.entries.slice(offset);
  }

  /** Returns all captured entries. */
  getEntries(): CapturedNetworkRequest[] {
    return [...this.entries];
  }

  isHttpsEnabled(): boolean {
    return this.httpsEnabled;
  }

  async stop(): Promise<void> {
    if (!this.started) return;

    try { this._adbShell(['settings', 'delete', 'global', 'http_proxy']); }
    catch { /* ignore */ }

    if (this.installedCertPath) {
      try { this._adbShell(['rm', '-f', this.installedCertPath]); }
      catch { /* ignore */ }
      this.installedCertPath = null;
    }

    try { await this.proxy?.stop(); } catch { /* ignore */ }

    this.started = false;
    this.httpsEnabled = false;
    this.pendingRequests.clear();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private async _collectResponse(res: ResLike): Promise<void> {
    const pending = this.pendingRequests.get(res.id);
    if (pending) this.pendingRequests.delete(res.id);

    const url = pending?.url ?? '';
    const method = pending?.method ?? 'GET';
    const startedAt = pending?.startedAt ?? new Date().toISOString();
    const startTs = pending?.startTimestamp ?? res.timingEvents?.startTimestamp ?? Date.now();
    const endTs = res.timingEvents?.responseSentTimestamp ?? Date.now();
    const responseTimeMs = Math.max(0, Math.round(endTs - startTs));

    let responseBody: string | null = null;
    try {
      const text = await res.body?.getText?.();
      if (text && text.length > 0) responseBody = text;
    } catch { /* body not buffered — skip */ }

    const responseHeaders = flattenHeaders(res.headers ?? {});
    // Content-type is read from the raw headers before redaction (it is not a
    // secret and is needed to classify the response).
    const contentType = responseHeaders['content-type'] ?? null;
    // Redact sensitive header values and JSON body fields BEFORE the entry is
    // stored/uploaded so Authorization/Cookie/Set-Cookie values and configured
    // body secrets never leave the machine in the clear.
    const redactedRequestHeaders = redactHeaders(
      pending?.requestHeaders ?? null,
      this.redactHeaderList,
    );
    const redactedResponseHeaders =
      redactHeaders(responseHeaders, this.redactHeaderList) ?? responseHeaders;
    const redactedResponseBody = redactBodyFields(responseBody, this.redactBodyFieldList);
    // Size reflects the redacted payload that is actually stored.
    const responseSize = redactedResponseBody
      ? Buffer.byteLength(redactedResponseBody, 'utf8')
      : 0;

    this.entries.push({
      url,
      method,
      resourceType: 'xhr',
      statusCode: res.statusCode,
      responseTimeMs,
      startedAt,
      requestHeaders: redactedRequestHeaders,
      requestBody: null,
      responseBody: redactedResponseBody,
      responseHeaders: redactedResponseHeaders,
      contentType,
      responseSize,
      requestBodyTruncated: false,
      responseBodyTruncated: false,
      isBinary: false,
      error: null,
    });
  }

  /**
   * Install CA cert to device trust store.
   * Tries system store first (requires remount), falls back to user store.
   */
  private _installCert(localPemPath: string, hash: string): string | null {
    // --- System trust store (trusted by all apps) ---
    try {
      execFileSync('adb', ['-s', this.deviceSerial, 'remount'],
        { stdio: 'pipe', timeout: 15_000 });
      const sysPath = `/system/etc/security/cacerts/${hash}.0`;
      execFileSync('adb', ['-s', this.deviceSerial, 'push', localPemPath, sysPath],
        { stdio: 'ignore', timeout: 15_000 });
      this._adbShell(['chmod', '644', sysPath]);
      process.stderr.write('[TestRelic] HTTPS proxy: CA cert installed to system trust store.\n');
      return sysPath;
    } catch { /* remount not available on API 29+ overlayfs */ }

    // --- User trust store (trusted by debug builds with <certificates src="user"/>) ---
    try {
      const userDir = '/data/misc/user/0/cacerts-added';
      this._adbShell(['mkdir', '-p', userDir]);
      const userPath = `${userDir}/${hash}.0`;
      execFileSync('adb', ['-s', this.deviceSerial, 'push', localPemPath, userPath],
        { stdio: 'ignore', timeout: 15_000 });
      this._adbShell(['chmod', '644', userPath]);
      this._adbShell(['chown', '1000:1000', userPath]);
      process.stderr.write(
        '[TestRelic] HTTPS proxy: CA cert installed to user trust store. ' +
        'HTTPS interception active for debug builds.\n',
      );
      return userPath;
    } catch (err) {
      process.stderr.write(
        `[TestRelic] HTTPS proxy: cert install failed (${(err as Error).message}). ` +
        'HTTPS capture unavailable.\n',
      );
      return null;
    }
  }

  private _adbShell(shellArgs: string[]): void {
    execFileSync(
      'adb',
      ['-s', this.deviceSerial, 'shell', ...shellArgs],
      { stdio: 'ignore', timeout: 10_000 },
    );
  }
}

// ---------------------------------------------------------------------------
// Local types (subset of mockttp's CompletedRequest / CompletedResponse)
// ---------------------------------------------------------------------------

interface ReqLike {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string | string[] | undefined>;
  timingEvents?: { startTime?: number; startTimestamp?: number };
}

interface ResLike {
  id: string;
  statusCode: number;
  headers: Record<string, string | string[] | undefined>;
  body?: { getText(): Promise<string | undefined> };
  timingEvents?: { startTimestamp?: number; responseSentTimestamp?: number };
}

function flattenHeaders(
  h: Record<string, string | string[] | undefined>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(h)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, Array.isArray(v) ? v.join(', ') : (v as string)]),
  );
}
